import { useQuery, useQueryClient } from '@tanstack/react-query';
import { createContext, useContext, useEffect, useMemo, useRef, type ReactNode } from 'react';
import { useOnline } from '@/components/notices';
import { usePrefs } from '@/stores/prefs';
import type { Membership, OrgRole, Profile } from '@/types/db';
import { authClient, db, unwrap } from './db';
import { isBackendConfigured } from './env';

export interface AppUser {
  id: string;
  email: string;
  name: string;
}

interface AuthContextValue {
  ready: boolean;
  user: AppUser | null;
  /** True when the user comes from the offline cache because the session could not be checked. */
  offlineSession: boolean;
  profile: Profile | null;
  memberships: Membership[];
  activeMembership: Membership | null;
  activeOrgId: string | null;
  setActiveOrgId: (id: string | null) => void;
  role: OrgRole | null;
  isEditor: boolean;
  isOrgAdmin: boolean;
  isClinicalApprover: boolean;
  isPlatformAdmin: boolean;
  refresh: () => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

const EDITOR_ROLES: OrgRole[] = ['educator', 'medical_director', 'admin', 'owner'];

/**
 * Managed Better Auth validates sessions over the network. A provider who reopens the PWA with no
 * signal must still reach saved protocols, so the last confirmed user is kept locally and used
 * only while the session cannot be checked. It grants no data access: the Data API still requires
 * a valid JWT, and cached data is all that is shown.
 */
const LAST_USER_KEY = 'ems-last-user';

function readLastUser(): AppUser | null {
  try {
    return JSON.parse(localStorage.getItem(LAST_USER_KEY) ?? 'null') as AppUser | null;
  } catch {
    return null;
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const online = useOnline();
  const { activeOrgId: storedOrgId, setActiveOrgId } = usePrefs();
  const session = authClient.useSession();
  const lastUser = useRef<AppUser | null>(readLastUser());

  const sessionUser = session.data?.user;
  const liveUser: AppUser | null = sessionUser ? { id: sessionUser.id, email: sessionUser.email, name: sessionUser.name } : null;
  const sessionUnavailable = !liveUser && (!online || Boolean(session.error));
  const user = liveUser ?? (sessionUnavailable ? lastUser.current : null);

  useEffect(() => {
    if (liveUser) {
      lastUser.current = liveUser;
      localStorage.setItem(LAST_USER_KEY, JSON.stringify(liveUser));
    } else if (!session.isPending && !session.error && online) {
      // Confirmed signed out.
      lastUser.current = null;
      localStorage.removeItem(LAST_USER_KEY);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [liveUser?.id, session.isPending, session.error, online]);

  const userId = user?.id;

  const profileQuery = useQuery({
    queryKey: ['me', 'profile', userId],
    enabled: Boolean(userId),
    queryFn: async () => unwrap(await db.from('profiles').select('*').eq('id', userId!).single()) as Profile,
  });

  const membershipsQuery = useQuery({
    queryKey: ['me', 'memberships', userId],
    enabled: Boolean(userId),
    queryFn: async () =>
      unwrap(
        await db
          .from('org_members')
          .select('org_id, user_id, role, status, certification_level, organization:organizations(*)')
          .eq('user_id', userId!)
          .eq('status', 'active'),
      ) as unknown as Membership[],
  });

  const memberships = useMemo(() => membershipsQuery.data ?? [], [membershipsQuery.data]);
  const profile = profileQuery.data ?? null;

  const activeMembership =
    memberships.find((m) => m.org_id === storedOrgId) ??
    memberships.find((m) => m.org_id === profile?.default_org_id) ??
    memberships[0] ??
    null;

  // Keep the persisted selection valid as memberships change.
  useEffect(() => {
    if (!membershipsQuery.isSuccess) return;
    const next = activeMembership?.org_id ?? null;
    if (next !== storedOrgId) setActiveOrgId(next);
  }, [membershipsQuery.isSuccess, activeMembership?.org_id, storedOrgId, setActiveOrgId]);

  const role = activeMembership?.role ?? null;
  const requireMd = activeMembership?.organization.settings?.require_md_approval ?? true;

  const value: AuthContextValue = {
    ready: !isBackendConfigured || !session.isPending || sessionUnavailable,
    user,
    offlineSession: !liveUser && Boolean(user),
    profile,
    memberships,
    activeMembership,
    activeOrgId: activeMembership?.org_id ?? null,
    setActiveOrgId,
    role,
    isEditor: role !== null && EDITOR_ROLES.includes(role),
    isOrgAdmin: role === 'admin' || role === 'owner',
    isClinicalApprover: role === 'medical_director' || (!requireMd && (role === 'admin' || role === 'owner')),
    isPlatformAdmin: profile?.is_platform_admin ?? false,
    refresh: async () => {
      await Promise.all([session.refetch(), profileQuery.refetch(), membershipsQuery.refetch()]);
    },
    signOut: async () => {
      await authClient.signOut();
      lastUser.current = null;
      localStorage.removeItem(LAST_USER_KEY);
      setActiveOrgId(null);
      queryClient.clear();
    },
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
}
